import { useEffect, useRef, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trophy, Swords, Percent, Star, Camera, Loader2, ShieldCheck, Copy, IdCard, Save, Megaphone } from "lucide-react";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState(null);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({ name: "", cpf: "", address: "", pix_key: "", other_wallets: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [pinInput, setPinInput] = useState("");
  const [savingPin, setSavingPin] = useState(false);

  useEffect(() => {
    api.get("/user/profile").then(({ data }) => {
      setProfile(data);
      setForm({ name: data.name || "", cpf: data.cpf || "", address: data.address || "", pix_key: data.pix_key || "", other_wallets: data.other_wallets || "" });
    });
    api.get("/user/history").then(({ data }) => setHistory(data)).catch(() => setHistory([]));
  }, []);

  const p = profile || user || {};
  const xpInLevel = (p.xp || 0) % 500;
  const pct = Math.round((xpInLevel / 500) * 100);
  const avatarSrc = p.picture || "https://api.dicebear.com/7.x/initials/svg?seed=" + (p.name || "P");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Imagem muito grande (máx 3MB).");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/user/upload-avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile((prev) => ({ ...prev, picture: data.picture }));
      await refresh();
      toast.success("Foto atualizada!");
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro ao enviar a foto."); }
    setUploading(false);
  };

  const saveProfile = async () => {
    if (form.name.trim().length < 2) return toast.error("Informe seu nome (mín. 2 caracteres).");
    setSaving(true);
    try {
      const { data } = await api.post("/user/profile", form);
      setProfile((prev) => ({ ...prev, ...data }));
      await refresh();
      toast.success("Cadastro atualizado!");
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro ao salvar."); }
    setSaving(false);
  };

  const savePin = async () => {
    if (!/^\d{4}$/.test(pinInput)) return toast.error("O PIN deve ter 4 dígitos.");
    setSavingPin(true);
    try {
      await api.post("/user/set-pin", { pin: pinInput });
      setPinInput("");
      await refresh();
      const { data } = await api.get("/user/profile");
      setProfile(data);
      toast.success("PIN de segurança salvo!");
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro ao salvar o PIN."); }
    setSavingPin(false);
  };

  const copyId = () => { navigator.clipboard?.writeText(p.user_id || ""); toast.success("ID copiado!"); };

  const stats = [
    { icon: Trophy, label: "Vitórias", value: p.wins || 0, color: "#10B981" },
    { icon: Swords, label: "Derrotas", value: p.losses || 0, color: "#EF4444" },
    { icon: Percent, label: "Aproveitamento", value: `${p.win_rate ?? 0}%`, color: "#D4AF37" },
    { icon: Star, label: "Pontos", value: p.points || 0, color: "#F59E0B" },
  ];

  const Field = ({ label, children, hint }) => (
    <div>
      <label className="block text-xs uppercase tracking-wider text-[#9CA3AF] mb-1">{label}</label>
      {children}
      {hint && <p className="text-[10px] text-[#6b7280] mt-1">{hint}</p>}
    </div>
  );
  const inputCls = "w-full bg-[#0F1319] border border-white/10 rounded-lg px-3 py-2.5 focus:border-[#D4AF37] outline-none";

  return (
    <div className="dr-grain min-h-screen bg-[#090B0E]">
      <Navbar />
      <main className="relative z-20 max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {p.admin_note ? (
          <div className="mb-6 flex items-start gap-3 rounded-xl border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-4 py-3" data-testid="admin-note-banner">
            <Megaphone size={18} className="text-[#D4AF37] mt-0.5 shrink-0" />
            <div><div className="text-xs uppercase tracking-wider text-[#D4AF37] font-bold">Aviso da administração</div><div className="text-sm text-[#F3E5AB]">{p.admin_note}</div></div>
          </div>
        ) : null}
        <div className="dr-card p-8 mb-6 flex flex-col sm:flex-row items-center gap-6" data-testid="profile-card">
          <div className="relative shrink-0">
            <img src={avatarSrc} alt="" className="w-24 h-24 rounded-2xl border-2 border-[#D4AF37]/40 object-cover" data-testid="profile-avatar" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="avatar-upload-btn"
              className="absolute -bottom-2 -right-2 w-9 h-9 rounded-full btn-gold flex items-center justify-center shadow-lg disabled:opacity-60" title="Trocar foto">
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={uploadAvatar} className="hidden" data-testid="avatar-file-input" />
          </div>
          <div className="flex-1 w-full text-center sm:text-left">
            <h1 className="font-head text-3xl font-black" data-testid="profile-name">{p.name}</h1>
            <p className="text-[#9CA3AF]">{money(p.credits)} • <span className="text-[#10B981]">{Number(p.virtual_credits || 0).toLocaleString("pt-BR")} fichas</span></p>
            <button onClick={copyId} data-testid="copy-id-btn" className="mt-2 inline-flex items-center gap-2 text-xs bg-[#0F1319] border border-white/10 rounded-full px-3 py-1.5 text-[#9CA3AF] hover:border-[#D4AF37]/50">
              <IdCard size={13} /> ID: <span className="font-mono-num text-[#F3E5AB]" data-testid="profile-id">{p.user_id}</span> <Copy size={12} />
            </button>
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-1"><span className="font-head font-bold">Nível {p.level || 1}</span><span className="font-mono-num text-[#9CA3AF]">{xpInLevel} / 500 XP</span></div>
              <div className="h-3 rounded-full bg-[#0F1319] overflow-hidden"><div className="h-full btn-gold transition-all duration-500" style={{ width: `${pct}%` }} /></div>
            </div>
          </div>
        </div>

        <div className="dr-card p-6 mb-6" data-testid="cadastro-card">
          <h2 className="font-head text-xl font-bold mb-1">Dados cadastrais</h2>
          <p className="text-[11px] text-[#9CA3AF] mb-4">Seu <b>nome</b> é o que aparece publicamente no ranking (pontos e fichas). O <b>ID</b> não pode ser alterado.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nome público"><input value={form.name} onChange={set("name")} maxLength={30} data-testid="profile-name-input" className={inputCls} /></Field>
            <Field label="CPF"><input value={form.cpf} onChange={set("cpf")} inputMode="numeric" placeholder="Somente números" data-testid="profile-cpf-input" className={inputCls} /></Field>
            <Field label="Endereço"><input value={form.address} onChange={set("address")} placeholder="Rua, número, cidade" data-testid="profile-address-input" className={inputCls} /></Field>
            <Field label="Chave Pix (para saque)" hint="Use seu CPF como chave Pix para receber saques."><input value={form.pix_key} onChange={set("pix_key")} placeholder="CPF / chave Pix" data-testid="profile-pix-input" className={inputCls} /></Field>
            <div className="sm:col-span-2">
              <Field label="Outras carteiras (anotações)" hint="Campo livre para suas anotações pessoais.">
                <textarea value={form.other_wallets} onChange={set("other_wallets")} rows={2} maxLength={500} data-testid="profile-wallets-input" className={inputCls} />
              </Field>
            </div>
          </div>
          <button onClick={saveProfile} disabled={saving} data-testid="save-profile-btn" className="mt-5 btn-gold px-6 py-3 rounded-full flex items-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Salvar cadastro
          </button>
        </div>

        <div className="dr-card p-6 mb-6" data-testid="pin-card">
          <h2 className="font-head text-xl font-bold mb-1 flex items-center gap-2"><ShieldCheck size={18} className="text-[#10B981]" /> PIN de Segurança</h2>
          <p className="text-[11px] text-[#9CA3AF] mb-4">Um PIN de 4 dígitos é exigido para confirmar saques e transferências. {p.has_pin ? <span className="text-[#10B981] font-semibold">PIN configurado ✓</span> : <span className="text-[#F59E0B] font-semibold">Ainda não configurado.</span>}</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input value={pinInput} onChange={(e) => setPinInput(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" maxLength={4} placeholder="Novo PIN (4 dígitos)" data-testid="pin-input"
              className="sm:w-56 bg-[#0F1319] border border-white/10 rounded-lg px-4 py-3 font-mono-num tracking-[0.4em] text-center focus:border-[#10B981] outline-none" />
            <button onClick={savePin} disabled={savingPin} data-testid="save-pin-btn" className="btn-gold px-6 py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-60">
              {savingPin ? <Loader2 size={16} className="animate-spin" /> : (p.has_pin ? "Alterar PIN" : "Criar PIN")}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((s) => { const Icon = s.icon; return (
            <div key={s.label} className="dr-card p-5" data-testid={`stat-${s.label}`}>
              <Icon size={20} style={{ color: s.color }} />
              <div className="font-mono-num font-black text-2xl mt-2">{s.value}</div>
              <div className="text-xs text-[#9CA3AF]">{s.label}</div>
            </div>
          ); })}
        </div>

        <div className="dr-card p-6" data-testid="history">
          <h2 className="font-head text-xl font-bold mb-4">Meu Histórico</h2>
          {history.length === 0 ? <p className="text-[#9CA3AF] text-sm">Nenhuma partida finalizada ainda.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-[#6b7280] text-left text-xs uppercase"><th className="py-2">Data</th><th>Adversário</th><th>Resultado</th><th>Entrada</th><th>Prêmio</th></tr></thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.match_id} className="border-t border-white/5">
                      <td className="py-3 text-[#9CA3AF]">{new Date(h.date).toLocaleDateString("pt-BR")}</td>
                      <td className="font-semibold">{h.opponent}</td>
                      <td><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${h.result === "vitória" ? "bg-[#10B981]/15 text-[#10B981]" : h.result === "empate" ? "bg-white/10 text-[#9CA3AF]" : "bg-[#EF4444]/15 text-[#EF4444]"}`}>{h.result}</span></td>
                      <td className="font-mono-num">{money(h.stake)}</td>
                      <td className="font-mono-num text-[#10B981]">{h.prize ? "+" + money(h.prize) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
